import { guardianFamilyApi } from '../../guardian/family/api';
/** admin/families — HOOKS. */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { adminFamiliesApi } from './api';
import { adminFamiliesKeys } from './keys';
import type { AdminCreateFamilyPayload, AdminUpdateFamilyPayload } from './types';

const invalidate = (qc: QueryClient) => qc.invalidateQueries({ queryKey: adminFamiliesKeys.all() });

export function useAdminFamilies(page: number, limit: number) {
  return useQuery({
    queryKey: adminFamiliesKeys.list({ page, limit }),
    queryFn: () => adminFamiliesApi.list(page, limit),
  });
}

export function useAdminFamilyDetail(id: string | null) {
  return useQuery({
    queryKey: adminFamiliesKeys.detail(id ?? ''),
    queryFn: () => adminFamiliesApi.getById(id!),
    enabled: !!id,
  });
}

export function useAdminCreateFamily() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AdminCreateFamilyPayload) => adminFamiliesApi.create(payload),
    onSuccess: () => invalidate(qc),
  });
}

export function useAdminUpdateFamily() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; payload: AdminUpdateFamilyPayload }) =>
      adminFamiliesApi.update(v.id, v.payload),
    onSuccess: () => invalidate(qc),
  });
}

/** Admin revocation refreshes the admin family's device list, not the guardian's cache. */
export function useAdminRevokeFamilyDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceId, familyId }: { deviceId: string; familyId: string }) => guardianFamilyApi.revokeDevice(deviceId, familyId),
    onSuccess: () => invalidate(qc),
  });
}

export function useAdminFamilyDevices(familyId: string) {
  return useQuery({
    queryKey: adminFamiliesKeys.devices(familyId),
    queryFn: () => guardianFamilyApi.listDevices(familyId),
  });
}
